import { Link } from '@tanstack/react-router'
import type { ReactElement } from 'react'

import { Badge } from '@/components/ui/badge'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { cn } from '@/lib/utils'

function normalizeDiscountCode(value: unknown): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve existing discount-code coercion before normalization
  return String(value ?? '')
    .trim()
    .toUpperCase()
}

function normalizeDiscountId(value: unknown): number | null {
  const id = Number(value)

  return Number.isInteger(id) && id > 0 ? id : null
}

function normalizeFallbackLabel(value: unknown): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve existing discount fallback label coercion
  return String(value ?? '').trim()
}

function DiscountBadge({ label, title = label }: { label: string; title?: string }): ReactElement {
  return (
    <Badge variant="outline" size="xs" title={title} className="min-w-0 shrink">
      <span className="truncate">{label}</span>
    </Badge>
  )
}

function DiscountCodeSummaryFallback({
  badgeLabel,
  className,
  showLabel,
}: {
  badgeLabel: string
  className?: string
  showLabel?: boolean
}): ReactElement {
  return (
    <span className={cn('inline-flex min-w-0 max-w-full items-center gap-1.5', className)}>
      {showLabel ? <span className="shrink-0">Discount</span> : null}
      {badgeLabel ? (
        <DiscountBadge label={badgeLabel} />
      ) : !showLabel ? (
        <span className="truncate">Discount</span>
      ) : null}
    </span>
  )
}

function DiscountCodeSummaryLinked({
  path,
  normalizedCode,
  normalizedDiscountId,
  className,
  showLabel,
}: LinkedSummaryProps & { path: ReturnType<typeof usePathHelper> }): ReactElement {
  const link = path.discounts.index().search({ discountId: normalizedDiscountId }).link

  return (
    <span className={cn('inline-flex min-w-0 max-w-full items-center gap-1.5', className)}>
      {showLabel ? <span className="shrink-0">Discount</span> : null}
      <Badge asChild interactive variant="outline" size="xs" className="min-w-0 shrink">
        <Link
          {...link}
          aria-label={`Open discount code ${normalizedCode}`}
          title={normalizedCode}
          onClick={(event) => event.stopPropagation()}
        >
          <span className="truncate">{normalizedCode}</span>
        </Link>
      </Badge>
    </span>
  )
}

export default function DiscountCodeSummaryLink({
  code,
  discountId,
  fallbackLabel,
  className,
  showLabel = true,
}: {
  code?: unknown
  discountId?: unknown
  fallbackLabel?: unknown
  className?: string
  showLabel?: boolean
}): ReactElement {
  const normalizedCode = normalizeDiscountCode(code)
  const normalizedDiscountId = normalizeDiscountId(discountId)
  const label = normalizeFallbackLabel(fallbackLabel) || 'Discount'
  const badgeLabel = normalizedCode || (label !== 'Discount' ? label : '')

  if (!normalizedCode || !normalizedDiscountId) {
    return (
      <DiscountCodeSummaryFallback
        badgeLabel={badgeLabel}
        className={className}
        showLabel={showLabel}
      />
    )
  }

  return (
    <DiscountCodeSummaryLinkWithPath
      normalizedCode={normalizedCode}
      normalizedDiscountId={normalizedDiscountId}
      className={className}
      showLabel={showLabel}
    />
  )
}

interface LinkedSummaryProps {
  normalizedCode: string
  normalizedDiscountId: number
  className?: string
  showLabel?: boolean
}

function DiscountCodeSummaryLinkWithPath({
  normalizedCode,
  normalizedDiscountId,
  className,
  showLabel,
}: LinkedSummaryProps): ReactElement {
  const path = usePathHelper()

  return (
    <Can I="list" a="discounts" passThrough>
      {(canListDiscounts) =>
        canListDiscounts ? (
          <Can I="view" a="discounts" passThrough>
            {(canViewDiscounts) =>
              canViewDiscounts ? (
                <DiscountCodeSummaryLinked
                  path={path}
                  normalizedCode={normalizedCode}
                  normalizedDiscountId={normalizedDiscountId}
                  className={className}
                  showLabel={showLabel}
                />
              ) : (
                <DiscountCodeSummaryFallback
                  badgeLabel={normalizedCode}
                  className={className}
                  showLabel={showLabel}
                />
              )
            }
          </Can>
        ) : (
          <DiscountCodeSummaryFallback
            badgeLabel={normalizedCode}
            className={className}
            showLabel={showLabel}
          />
        )
      }
    </Can>
  )
}
